/**
 * 视讯登录凭据 SSE 长连接
 *
 * 见 docs/25-live-frontend-integration.md 第二/三节 (v3, 2026-09-17 更新)。
 * launch 响应只回 session_id, 登录凭据 (username/password + access_token) 100% 走这条 SSE 异步推送。
 *
 * 事件流本身是加密的, 跟普通接口同一套 RSA+AES 信封方案, 只是密钥放请求头
 * (`X-Encrypted-Key`) 而不是请求体里 (GET 没有请求体); 不带这个头服务端直接
 * 400/170001, 不会降级明文。
 *
 * ⚠️ 必须带三个头: Authorization + X-Device-Fingerprint + X-Encrypted-Key, 少一个都进不去。
 * 最容易漏的是 X-Device-Fingerprint —— 这个端点跟其他加密接口一样过 AuthMiddleware,
 * 必须带一模一样的指纹值。漏了/不匹配的现象极具迷惑性: HTTP 状态仍是 200 (不是 401/403),
 * body 却是 `{"code":100006,"msg":"缺少设备指纹"}`, 表现为"连上了但永远收不到事件"。
 * 所以下面 onopen 里不能只看 response.ok, 必须连 content-type 一起判断。
 *
 * 不能用浏览器原生 EventSource: 它不支持自定义请求头, 而这个端点要带标准
 * Authorization 鉴权 + 设备指纹 + 加密密钥, 三个都是自定义头。
 */
import { fetchEventSource } from '@microsoft/fetch-event-source'
import {
  getAccessToken,
  getDeviceFingerprint,
  generateEncryptionEnvelope,
  decryptResponse,
  isWebCryptoAvailable,
} from '@/services/request'
import type { LiveTokenEvent } from '@/types/api'

// 服务端等 60 秒后推 timeout 事件: 不要自动重连/重新 launch, 提示玩家稍后重试
// (此刻钱已全额转入视讯侧, 后端定时任务 5 分钟内会自动带回主钱包)
export class LiveTokenTimeoutError extends Error {
  constructor() {
    super('LIVE_TOKEN_TIMEOUT')
    this.name = 'LiveTokenTimeoutError'
  }
}

// SSE 返回 403 (业务码 200006): session_id 不属于当前用户, 或距 launch 超过 5 分钟归属记录已过期
// 处理方式是重新 launch, 跟"会话校验失败"(503, 应重试同一个 session_id) 刚好相反, 不要合并成一个分支
export class LiveSessionInvalidError extends Error {
  constructor() {
    super('LIVE_SESSION_INVALID')
    this.name = 'LiveSessionInvalidError'
  }
}

interface EncryptedEventPayload {
  encrypted_data: string
  iv: string
}

/**
 * 连接 SSE 等待视讯登录凭据 (username/password + access_token + h5_url)。
 * 拿到 launch 返回的 session_id 后应立刻调用, 不要等
 * (凭据通常 1 秒内到达; 服务端先缓存后广播, 晚连也能拿到, 不用担心抢时序)。
 *
 * ⚠️ resolve 出来的 username/password 是明文凭据, 调用方用完即弃:
 * 不要写进 localStorage/sessionStorage/cookie, 不要打进 console/埋点/日志/错误上报。
 */
export async function waitLiveToken(sessionId: string): Promise<LiveTokenEvent> {
  if (!isWebCryptoAvailable()) {
    throw new Error('当前环境不支持 Web Crypto, 无法安全获取视讯登录凭据')
  }

  // 本次连接专用的一次性 AES 密钥: 用服务端 RSA 公钥加密后放请求头, 后续用它解密收到的每条消息
  const { encryptedKey, aesKey } = await generateEncryptionEnvelope()
  const jwt = getAccessToken()

  return new Promise((resolve, reject) => {
    const ctrl = new AbortController()

    fetchEventSource(`/api/v1/game/live/token/stream?session_id=${encodeURIComponent(sessionId)}`, {
      headers: {
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        // 必须与登录时一致, 漏了/不匹配都会被 AuthMiddleware 拦下 (见文件头说明)
        'X-Device-Fingerprint': getDeviceFingerprint(),
        'X-Encrypted-Key': encryptedKey,
      },
      signal: ctrl.signal,
      // 只等一个 60 秒内的一次性结果: 页面切到后台也保持连接, 不因不可见而断开重连
      openWhenHidden: true,
      async onopen(response) {
        const contentType = response.headers.get('content-type') || ''
        if (response.ok && contentType.includes('text/event-stream')) {
          return // 真正建立了事件流, 交给 onmessage 继续处理
        }
        if (response.status === 403) {
          throw new LiveSessionInvalidError()
        }
        // 状态码可能仍是 200 (业务错误码塞在 JSON body 里, 典型的是 100006 缺设备指纹/
        // 指纹不匹配), 也可能是其它非 2xx/非 event-stream 响应, 统一读出 body 帮助排查,
        // 绝不能只凭 response.ok 判断"连上了" —— 那样会一直卡到 60 秒超时才失败
        let detail = ''
        try {
          detail = (await response.clone().text()).slice(0, 200)
        } catch {
          // 读取失败就算了, 下面仍然抛出状态码本身
        }
        throw new Error(`视讯登录凭据 SSE 连接失败: HTTP ${response.status} ${detail}`)
      },
      onmessage(ev) {
        if (ev.event === 'token') {
          ctrl.abort()
          const { encrypted_data, iv } = JSON.parse(ev.data) as EncryptedEventPayload
          decryptResponse<LiveTokenEvent>(encrypted_data, iv, aesKey).then(resolve, reject)
        } else if (ev.event === 'timeout') {
          ctrl.abort()
          reject(new LiveTokenTimeoutError())
        }
        // `: ping` 心跳注释行 fetchEventSource 不会当作 message 派发, 无需处理
      },
      onerror(err) {
        // ⚠️ 这里绝不能调用 ctrl.abort(): fetchEventSource 内部给外部传入的 signal 注册了
        // 'abort' 监听器, 一旦 abort 会同步 resolve 它自己内部的 Promise (语义是"调用方主动
        // 取消, 当正常结束处理"); 等下面这行 throw 被库自己的 catch 接住去调它自己的 reject()
        // 时, Promise 早就因为上面那次 resolve 定型了, reject 变成空操作没人收得到 ——
        // 外层 waitLiveToken() 的 Promise 就会永远卡在 pending, 界面表现为"卡住不动没有任何
        // 提示"(2026-09-17 视讯 403 场景就是这么卡住的, 参考实现里的写法有这个坑, 不要照抄)。
        // 直接 throw 就够了: 库自己 catch 到后会 dispose() 清理并 reject 它内部的 Promise,
        // 经 .catch(reject) 正确传到这里的 reject, 同时也阻止了默认的自动重连。
        throw err instanceof Error ? err : new Error(String(err))
      },
    }).catch(reject)
  })
}
